import { Timeout } from 'react-number-format/types/types'

import { ColumnConfig, ColumnKey, DataGridTheme, LeftRight } from '../DataGrid.types'
import { getColumnsGroupingSetup } from './columnGrouping'
import { getStyleElement } from './globalStyling'

interface StickyColumn {
  element: HTMLDivElement
  isGroupCell: boolean
  groupId?: string
  key: string
  index: number
  width: number
  height: number
}

const HEADER_HEIGHT_VAR = '--header-full-height'
const HEADER_GROUPS_HEIGHT_VAR = '--header-groups-height'

export class StickyController {
  hasColumnGrouping = false
  everyColumnHasGroup = false
  updateVariablesTimeoutRef: Timeout | undefined = undefined

  readonly styleElement
  readonly dataGridId
  readonly gridTheme
  readonly columnsLength
  readonly headerCells: Record<string, StickyColumn> = {}

  readonly observer = new ResizeObserver(entries => {
    entries.forEach(entry => {
      const headerCell = Object.values(this.headerCells).find(cell => {
        return cell.element === entry.target
      })
      if (headerCell === undefined) {
        throw Error(`No stickyColum for ResizeObserver entry`)
      }

      // The property clientHeight will round the value to an integer. So we need this method to remove rounding.
      const heightRoundDifference = Number.isInteger(entry.contentRect.height)
        ? 0
        : entry.contentRect.height % 1

      headerCell.height = entry.target.clientHeight - heightRoundDifference
      headerCell.width = entry.target.clientWidth
    })
    this.updateVariables()
  })

  constructor(dataGridId: string, theme: DataGridTheme, columnsLength: number) {
    this.dataGridId = dataGridId
    this.styleElement = getStyleElement(`sticky-vars-${dataGridId}`)
    this.gridTheme = theme
    this.columnsLength = columnsLength
    this.delayedUpdateVariables = this.delayedUpdateVariables.bind(this)
  }

  registerHeaderCell = (
    headerCell: HTMLDivElement,
    key: string,
    columnIndex: number,
    groupId?: string,
    isGroupCell = false,
  ) => {
    this.observer.observe(headerCell)

    this.unregister(key)

    const height = headerCell.clientHeight
    const width = headerCell.clientWidth

    this.headerCells[key] = {
      element: headerCell,
      key,
      index: columnIndex,
      width,
      height,
      isGroupCell,
      groupId,
    }
  }

  unregister = (columnKey: ColumnKey): boolean => {
    if (this.headerCells[columnKey]) {
      this.observer.unobserve(this.headerCells[columnKey].element)
      Reflect.deleteProperty(this.headerCells, columnKey)
      this.updateVariables()
      return true
    }
    return false
  }

  updateColumnsGroupingSetup<RowType>(columns: ColumnConfig<RowType>[]) {
    const { hasColumnGrouping, everyColumnHasGroup } = getColumnsGroupingSetup(columns)
    this.hasColumnGrouping = hasColumnGrouping
    this.everyColumnHasGroup = everyColumnHasGroup
  }

  updateVariables() {
    // Below timeout logic prevents calling updateVariables multiple times on multiple columns unregister
    if (this.updateVariablesTimeoutRef) {
      //clearTimeout(this.updateVariablesTimeoutRef)
    }
    this.updateVariablesTimeoutRef = setTimeout(this.delayedUpdateVariables)
  }

  delayedUpdateVariables() {
    this.updateVariablesTimeoutRef = undefined
    const allCells = Object.values(this.headerCells)
    const groupsRowHeight = this.hasColumnGrouping
      ? allCells
          .filter(({ isGroupCell }) => isGroupCell)
          .reduce((max, currentCell) => Math.max(max, currentCell.height), 0)
      : 0

    const headerCellsRowHeight = allCells
      .filter(
        ({ isGroupCell, groupId }) =>
          !isGroupCell && (!this.hasColumnGrouping || this.everyColumnHasGroup || groupId),
      )
      .reduce((max, currentCell) => {
        return Math.max(max, currentCell.height)
      }, 0)

    const filteredCells = allCells
      .filter(({ isGroupCell }) => !isGroupCell)
      .sort((a, b) => a.index - b.index)

    const getWidthOffsets = (cells: StickyColumn[], namePostfix: LeftRight, indexOffset: number) =>
      cells.reduce(
        ({ varString, offset }, { index, width }) => {
          const newOffset = offset + width
          const currentVar = `${this.getColumnVarName(
            index + indexOffset,
            namePostfix,
          )}: ${newOffset}px;`
          return {
            varString: `${varString} ${currentVar}`,
            offset: newOffset,
          }
        },
        {
          varString: '',
          offset: 0,
        },
      )

    const offsetsLeft = getWidthOffsets(filteredCells, LeftRight.Left, 1)
    const offsetsRight = getWidthOffsets(filteredCells.reverse(), LeftRight.Right, -1)

    const fullHeaderHeight = headerCellsRowHeight + groupsRowHeight

    const headerSizes = `${this.getHeaderHeightVar()}: ${fullHeaderHeight}px; ${this.getHeaderGroupsHeightVar()}: ${groupsRowHeight}px;`

    this.styleElement.innerHTML = `#${this.dataGridId} { ${offsetsLeft.varString} ${offsetsRight.varString} ${headerSizes} }`
  }

  getColumnVarName(columnIndex: number, postfix: LeftRight) {
    return `--column-${columnIndex}-${postfix}-${this.dataGridId}`
  }

  getLeftVarForColumn(columnIndex: number) {
    if (columnIndex === 0) {
      return undefined
    }
    return this.getColumnVarName(columnIndex, LeftRight.Left)
  }

  getRightVarForColumn(columnIndex: number) {
    if (columnIndex === this.columnsLength - 1) {
      return undefined
    }
    return this.getColumnVarName(columnIndex, LeftRight.Right)
  }

  getHeaderHeightVar() {
    return `${HEADER_HEIGHT_VAR}-${this.dataGridId}`
  }

  getHeaderGroupsHeightVar() {
    return `${HEADER_GROUPS_HEIGHT_VAR}-${this.dataGridId}`
  }
}
